import { Trash2 } from 'lucide-react';

import type { TToolArgumentEditor, TToolArgumentValueType } from '@intone/shared/types';
import { isSecretHeader } from '@intone/shared/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import {
  TextInput,
  NumberInput,
  TextareaInput,
  CheckboxInput,
  ListKeyValueInput,
  ChoiceInput,
} from './ToolArgumentViews';

type ToolArgumentViewProps = {
  arg: TToolArgumentEditor;
  onChange: (patch: Partial<TToolArgumentEditor>) => void;
  onRemove: (id: string) => void;
  allowRename: boolean;
  isError: boolean;
};

export function ToolArgumentView({ arg, onChange, onRemove, allowRename, isError }: ToolArgumentViewProps) {
  const handleValueChange = (value: TToolArgumentEditor['value']) => {
    onChange({ value });
  };

  const isSecret = isSecretHeader(arg.name);

  const renderInput = () => {
    const inputProps = { arg, onChange: handleValueChange, isError };

    // Header args with secret-like names get a password input
    if (isSecret) {
      const hasError = isError && arg.is_required && !String(arg.value).trim();
      return (
        <Input
          value={String(arg.value)}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={arg.placeholder || 'Enter value'}
          className={`h-7 ${hasError ? 'border-destructive' : ''}`}
          type="password"
          autoComplete="off"
          id={arg.name}
        />
      );
    }

    switch (arg.argType) {
      case 'number':
        return <NumberInput {...inputProps} />;
      case 'checkbox':
        return <CheckboxInput {...inputProps} />;
      case 'text':
        return <TextareaInput {...inputProps} />;
      case 'fixed':
        return <TextInput {...inputProps} />;
      case 'list_key_value':
        return <ListKeyValueInput arg={arg} onChange={onChange} isError={isError} />;
      case 'choice':
        return <ChoiceInput {...inputProps} />;
      default:
        // Legacy: no argType defined, use valueType to pick input
        if (arg.valueType === 'fixed_value') {
          return <TextInput {...inputProps} />;
        }
        return <TextareaInput {...inputProps} />;
    }
  };

  // Checkbox has its own label inside the component
  if (arg.argType === 'checkbox') {
    return (
      <div className="grid gap-2 border rounded-md p-3 border-gray-300">
        {renderInput()}
      </div>
    );
  }

  return (
    <div className="grid gap-2 border rounded-md p-3 border-gray-300">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 grid gap-1">
          {allowRename ? (
            <Input
              value={arg.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="argument_name"
              className="h-7"
              id={`arg.name_${arg.id}`}
            />
          ) : (
            <Label htmlFor={arg.name} className="font-semibold text-gray-700">
              <span className="flex items-center gap-2">
                {arg.name.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())}
                {arg.is_required && <span className="text-destructive ml-1">*</span>}
              </span>
            </Label>
          )}
          {arg.description && (
            <p className="text-detail text-gray-500">{arg.description}</p>
          )}
        </div>

        {!arg.argType && (
          <Select
            value={arg.valueType}
            onValueChange={(v: TToolArgumentValueType) => onChange({ valueType: v })}
          >
            <SelectTrigger className="h-7 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed_value">Fixed Value</SelectItem>
              <SelectItem value="llm_value">LLM Prompt</SelectItem>
            </SelectContent>
          </Select>
        )}

        {!arg.fromRequired && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 w-6 p-0 text-destructive hover:text-white hover:bg-destructive border-destructive hover:border-destructive"
            onClick={() => onRemove(arg.id)}
          >
            <Trash2 className="w-2.5 h-2.5" />
          </Button>
        )}
      </div>

      {renderInput()}
    </div>
  );
}
